/**
 * Adds MediaStream recording functionality to Trix editors.
 *
 * Creates "Record Audio" and "Record Video" buttons that initiate MediaStream recording.
 *
 * Example: %div{ data: {controller: "trix--editor trix--media-recording"} }
 *
 */
import { Controller } from "@hotwired/stimulus";
import Trix from "trix";
import { Modal } from "bootstrap";

export default class extends Controller {
  static values = {
    i18nContent: { type: Object, default: {} },
    maxDuration: { type: Number, default: 300 }, // 5 minutes default
  };

  connect() {
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.mediaStream = null;
    this.recordingType = null;
    this.recordingOverlay = null;
    this.bootstrapModal = null;

    this.element.addEventListener(
      "trix-initialize",
      () => {
        this.setupMediaButtons();
      },
      { once: true },
    );
  }

  disconnect() {
    this.cancelRecording();
    this.removeOverlay();
  }

  setupMediaButtons() {
    const toolbar = this.element.parentElement.querySelector("trix-toolbar");
    if (!toolbar) return;

    const audioButton = document.createElement("button");
    const videoButton = document.createElement("button");

    audioButton.classList.add("trix-button", "trix-button--icon", "trix-button--icon-file");
    audioButton.type = "button";
    audioButton.title = this.i18nContentValue.record_audio;
    audioButton.innerHTML = `<i class="fas fa-microphone"></i>`;
    audioButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.startRecording("audio");
    });

    videoButton.classList.add("trix-button", "trix-button--icon", "trix-button--icon-file");
    videoButton.type = "button";
    videoButton.title = this.i18nContentValue.record_video;
    videoButton.innerHTML = `<i class="fas fa-video"></i>`;
    videoButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.startRecording("video");
    });

    toolbar.querySelector(".trix-button-group--file-tools").appendChild(audioButton);
    toolbar.querySelector(".trix-button-group--file-tools").appendChild(videoButton);
  }

  async startRecording(type) {
    this.recordingType = type;
    this.recordedChunks = [];

    const constraints =
      type === "video"
        ? {
            video: {
              width: { ideal: 640 },
              height: { ideal: 480 },
              frameRate: { ideal: 24, max: 30 },
            },
            audio: true,
          }
        : { audio: true };

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.showRecordingOverlay(type);
    } catch (error) {
      this.showError(this.i18nContentValue.error);
    }
  }

  beginRecording() {
    const mimeType = this.getSupportedMimeType();

    const options = { mimeType: mimeType };
    if (this.recordingType === "video") {
      options.videoBitsPerSecond = 1000000; // 1 Mbps for smaller file sizes
    }

    this.mediaRecorder = new MediaRecorder(this.mediaStream, options);

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    this.mediaRecorder.start(1000);
    this.startTimer();
  }

  getSupportedMimeType() {
    const types =
      this.recordingType === "video"
        ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
        : ["audio/webm;codecs=opus", "audio/webm", "audio/ogg", "audio/mp4"];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return types[0];
  }

  showRecordingOverlay(type) {
    this.recordingOverlay = document.createElement("div");
    this.recordingOverlay.className = "modal fade";
    this.recordingOverlay.setAttribute("tabindex", "-1");
    this.recordingOverlay.setAttribute("data-bs-backdrop", "static");
    this.recordingOverlay.setAttribute("data-bs-keyboard", "false");
    this.recordingOverlay.innerHTML = `
      <div class="modal-dialog modal-dialog-centered media-recording-modal">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <span class="recording-indicator"></span>
            <h5 class="modal-title ms-2">${type === "video" ? this.i18nContentValue.video_recording_title : this.i18nContentValue.audio_recording_title}</h5>
          </div>
          <div class="modal-body text-center">
            ${type === "video" ? '<video class="media-recording-preview" autoplay muted playsinline></video>' : '<div class="audio-visualizer"><i class="fas fa-microphone fa-3x"></i></div>'}
            <span class="recording-timer font-monospace fs-5 d-none">00:00</span>
          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="btn btn-danger media-recording-toggle">
              <i class="fas fa-circle"></i> ${this.i18nContentValue.start_recording}
            </button>
            <button type="button" class="btn btn-secondary media-recording-cancel" data-bs-dismiss="modal">
              ${this.i18nContentValue.cancel}
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.recordingOverlay);

    this.bootstrapModal = new Modal(this.recordingOverlay);
    this.bootstrapModal.show();

    if (type === "video") {
      const preview = this.recordingOverlay.querySelector(".media-recording-preview");
      preview.srcObject = this.mediaStream;
    }

    this.recordingOverlay
      .querySelector(".media-recording-toggle")
      .addEventListener("click", () => this.toggleRecording());

    this.recordingOverlay
      .querySelector(".media-recording-cancel")
      .addEventListener("click", () => this.cancelRecording());
  }

  toggleRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.stopRecording();
    } else {
      this.beginRecording();
      this.updateRecordingButton(true);
    }
  }

  updateRecordingButton(isRecording) {
    const button = this.recordingOverlay?.querySelector(".media-recording-toggle");
    const indicators = this.recordingOverlay?.querySelectorAll(
      ".recording-indicator, .audio-visualizer",
    );
    const timer = this.recordingOverlay?.querySelector(".recording-timer");

    if (button) {
      if (isRecording) {
        button.innerHTML = `<i class="fas fa-stop"></i> ${this.i18nContentValue.stop_recording}`;
        button.setAttribute("data-bs-dismiss", "modal");
        if (indicators) indicators.forEach((el) => el.classList.add("recording"));
        if (timer) timer.classList.remove("d-none");
      } else {
        button.innerHTML = `<i class="fas fa-circle"></i> ${this.i18nContentValue.start_recording}`;
        button.removeAttribute("data-bs-dismiss");
        if (indicators) indicators.forEach((el) => el.classList.remove("recording"));
        if (timer) timer.classList.add("d-none");
      }
    }
  }

  startTimer() {
    this.recordingStartTime = Date.now();
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
      const minutes = Math.floor(elapsed / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (elapsed % 60).toString().padStart(2, "0");

      const timerElement = this.recordingOverlay?.querySelector(".recording-timer");
      if (timerElement) {
        timerElement.textContent = `${minutes}:${seconds}`;
      }

      if (elapsed >= this.maxDurationValue) {
        this.stopRecording();
      }
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  stopRecording() {
    this.stopTimer();

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }

    this.stopMediaStream();
    this.processRecording();
  }

  cancelRecording() {
    this.stopTimer();

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }

    this.stopMediaStream();
    this.recordedChunks = [];
    this.removeOverlay();
  }

  stopMediaStream() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  removeOverlay() {
    if (this.bootstrapModal) {
      this.bootstrapModal = null;
    }
    if (this.recordingOverlay) {
      this.recordingOverlay.remove();
      this.recordingOverlay = null;
    }
  }

  processRecording() {
    if (this.recordedChunks.length === 0) {
      this.removeOverlay();
      return;
    }

    const mimeType = this.getSupportedMimeType();
    const cleanMimeType = mimeType.split(";")[0];
    const blob = new Blob(this.recordedChunks, { type: mimeType });

    let extension = "webm";
    if (cleanMimeType.includes("mp4")) {
      extension = "mp4";
    } else if (cleanMimeType.includes("ogg")) {
      extension = "ogg";
    }

    const filename = `${this.recordingType}-recording-${Date.now()}.${extension}`;
    const file = new File([blob], filename, { type: cleanMimeType });

    this.attachFileToEditor(file);
    this.removeOverlay();
  }

  attachFileToEditor(file) {
    const editor = this.element.editor;
    if (!editor) return;

    const acceptEvent = new CustomEvent("trix-file-accept", {
      bubbles: true,
      cancelable: true,
      detail: { file: file },
    });
    acceptEvent.file = file;

    if (!this.element.dispatchEvent(acceptEvent)) {
      return;
    }

    const attachment = Trix.Attachment.attachmentForFile(file);
    editor.insertAttachment(attachment);
  }

  showError(message) {
    alert(message);
  }
}
